import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import _ from 'lodash';
import { getSubscription, getSubscriptionStatus, selectUser } from '../../../reducers/user.selectors';
import { selectActiveLicences } from '../../../reducers/licenceModels.selectors';
import useLicenceModel from '../hooks/useLicenceModel';
import useLibraryIpValidation from '../hooks/useLibraryIpValidation';
import AppNavBar from '../../../components/AppLayout/MasterLayout/AppNavBar';
import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { routes, userType, packageLicenceIds, newPricingPackageLicenceIds } from '../../../constants';
const { meinUnterricht360KI } = newPricingPackageLicenceIds;
import useRouter from '../../../utils/useRouter';
import {
  isPaused as checkIfPaused,
  isSuspended as checkIfSuspended
} from '../../../utils/subscription';
import { useLicenceIdFromUrl } from '../hooks/useLicenceIdFromUrl';
import { customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';
import LicenceInfo from '../LicenceInfo';
import { DiscountInformation } from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import { PaymentPeriod } from '@klab-berlin/api-sdk/lib/types/common';
import PaymentForm from '../PaymentForm';

export enum PaymentMode {
  PremiumUpgrade,
  PremiumRegistration,
}

interface PaymentPageProps {
  paymentMode: PaymentMode;
}

interface ColumnContainerProps {
  isLargeScreen: boolean;
  dataTestId: string;
  bgcolor: string;
}

const ColumnContainer: React.FC<ColumnContainerProps> = ({ dataTestId, bgcolor, isLargeScreen, children }) => {
  const { paymentInformationContainer } = customSizes;

  return (
    <Box
      data-testid={dataTestId}
      flex={1}
      bgcolor={bgcolor}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        paddingY: isLargeScreen ? paymentInformationContainer.paddingY : spaces.xxl,
      }}
    >
      <Box
        sx={{ 
          width: isLargeScreen ? 
            paymentInformationContainer.width.desktop : 
            paymentInformationContainer.width.mobile 
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const PaymentPage: React.FC<PaymentPageProps> = (props) => {
  const { t } = useTranslation();
  const { isLgScreen, isMdScreen } = useScreenSize();
  const isDesktopScreen = isLgScreen || isMdScreen;
  const { paymentMode } = props;
  const { match: { location: { query } }, router } = useRouter();
  const { meinUnterricht360ForDocumentUsers } = packageLicenceIds;
  const [promoCode, setPromoCode] = useState<string>(query.promoCode || '');
  const [promoCodeDiscountInfo, setPromoCodeDiscountInfo] = useState<DiscountInformation>();
  const paymentRhythms = ['monthly', 'annually'] as PaymentPeriod[];
  const [selectedPaymentRhythm, setSelectedPaymentRhythm] = useState<PaymentPeriod>(paymentRhythms[0]);
  const user = useSelector(selectUser);
  const subscription = useSelector(getSubscription);
  const subscriptionStatus = useSelector(getSubscriptionStatus);
  const licenceModels = useSelector(state => selectActiveLicences(state));
  const isPaused = checkIfPaused(subscription);
  const isSuspended = checkIfSuspended(subscription);
  const urlLibrary = query.library;
  const urlRedirect = query.optin_redirect;
  const urlLicenceId = useLicenceIdFromUrl();
  const { hasValidLibraryIp, isLibraryReady } = useLibraryIpValidation({ urlLibrary });
  const { selectedLicenceModel, toggleSelectedLicenceModel } = useLicenceModel({
    licenceModels,
    urlLicenceId,
    isLibraryReady,
    hasValidLibraryIp
  });

  // SM-92 Phase 6: Stripe-billed users (incl. basis-with-stripeCustomerId)
  // should never land on the legacy Zuora payment page. Bounce them over to
  // the new `/upgrade-options` flow. Basis users without a Stripe customer
  // record keep the legacy flow.
  useEffect(() => {
    if (user?.isStripeUser) {
      router.push(`/${routes.stripeUpgrade.root}`);
    }
  }, [user?.isStripeUser]);

  useEffect(() => {
    /**
     * PAYMENT PAGE REDIRECT LOGIC - SIMPLIFIED
     * 
     * This useEffect handles redirect logic based on user type, subscription, and pricing model.
     * 
     * MAIN RULES:
     * 1. Premium users with old pricing → blocked from new pricing plans
     * 2. Basis users → blocked from old pricing plans (except courses)
     * 3. Document users with old pricing → blocked from new pricing plans
     * 4. Document users with new pricing → blocked from old pricing plans
     * 
     * SPECIAL CASES:
     * - Trial/Kombi users → always redirected
     * - Users with canUpgrade=false → always redirected
     * - Sachsen-Anhalt licence users → dashboard redirected
     * 
     */
    // SM-92 Phase 6: Stripe users are owned by the new `/upgrade-options`
    // flow (handled by the effect above). Skip the legacy redirect logic
    // entirely for them so it can't `router.push` to `upgradePackageSelection`
    // / dashboard before the Stripe redirect lands. Basis users without a
    // Stripe customer record stay on the legacy flow below.
    if (user?.isStripeUser) return;

    if (user && selectedLicenceModel && !_.isEmpty(licenceModels)) {
      // For premium users, wait for subscription data to be loaded
      if (user.type !== userType.basis && !subscription) {
        return;
      }
      const { isInTrial, isKombiUser } = user;
      const { licenceModelId: selectedLicenceId } = selectedLicenceModel;
      const isBibliothekUser = user?.type === userType.document;
      const currentLicenceId =
        subscription?.licence?.licenceModelId ?
          Number(subscription.licence.licenceModelId) :
          null;
      const isLicenceModelSelected = currentLicenceId && Number(currentLicenceId) === selectedLicenceId;
      const isSAlicence = currentLicenceId === 5670;

      // Check if selected licence uses new pricing
      const selectedPlanUsesNewPricing = selectedLicenceModel?.licenceModelId && 
        Object.values(newPricingPackageLicenceIds).includes(selectedLicenceModel.licenceModelId);

      // Check if current user's licence uses new pricing (only if user has subscription)
      const currentPlanUsesNewPricing = currentLicenceId && 
        Object.values(newPricingPackageLicenceIds).includes(Number(currentLicenceId));

      const isUserTypeAllowedToUpgrade = user?.type && selectedLicenceModel?.allowedUserTypes?.includes(user.type);
      // Document user restrictions (backward compatible with new pricing enhancement)
      const allowedDocumentUserLicence = (currentPlanUsesNewPricing && selectedPlanUsesNewPricing) ?
        meinUnterricht360KI :
        meinUnterricht360ForDocumentUsers;

      const isNotAddOnSubscriptionForDocumentUser = isBibliothekUser &&
        selectedLicenceModel?.licenceModelId !== allowedDocumentUserLicence;

      // Block basis users from old pricing plans
      const shouldBlockBasisUsersFromOldPricing =
        !subscription && 
        !selectedPlanUsesNewPricing && 
        selectedLicenceModel?.licenceModelId !== packageLicenceIds.meineFortbildung &&
        user.type === userType.basis;

      // Consolidated blocking rules
      const shouldBlockUpgrade = 
        // Block premium users with old pricing from new pricing plans
        (subscription && !currentPlanUsesNewPricing && selectedPlanUsesNewPricing) ||
        // Block basis users from old pricing plans
        shouldBlockBasisUsersFromOldPricing ||
        // Block document users with old pricing from new pricing plans
        (isBibliothekUser && currentLicenceId && !currentPlanUsesNewPricing && selectedPlanUsesNewPricing) ||
        // Block document users with new pricing from old pricing plans
        (isBibliothekUser && currentLicenceId && currentPlanUsesNewPricing && !selectedPlanUsesNewPricing);

      if (shouldBlockUpgrade) {
        router.push(`/${routes.upgradePackageSelection.root}`);
        return;
      }

      // Final redirect conditions (backward compatible with new pricing support)
      const shouldRedirect = 
        isInTrial ||
        isKombiUser ||
        user.canUpgrade === false ||
        !isUserTypeAllowedToUpgrade ||
        isLicenceModelSelected ||
        isNotAddOnSubscriptionForDocumentUser;

      if (shouldRedirect) {
        router.push(`/${routes.upgradePackageSelection.root}`);
      }

      // If the user has a Sachsen-Anhalt licence, we redirect to the dashboard
      if (isSAlicence) {
        router.push(`/${routes.dashboard.root}`);
      }
    }  
  }, [selectedLicenceModel, licenceModels, user, subscription]);

  const isLicencesDataReady = () => !_.isEmpty(selectedLicenceModel);

  if (paymentMode === PaymentMode.PremiumUpgrade){
    if (!subscriptionStatus || ![AsyncActionStatus.READY, AsyncActionStatus.NOTFOUND].includes(subscriptionStatus)){
      return <></>;
    }

    if (isPaused || isSuspended) {
      router.push(
        user?.isStripeUser
          ? `/${routes.stripeUpgrade.root}`
          : `/${routes.upgradePackageSelection.root}`,
      );
    }
  }

  return (
    <Box 
      data-testid='payment-page-container'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <AppNavBar title={t('Payment')}/>
      {isLicencesDataReady() && (
        <Box
          data-testid='payment-page-content'
          sx={{
            display: 'flex',
            flexDirection: isDesktopScreen ? 'row' : 'column',
            flex: 1,
          }}
        >
          <ColumnContainer 
            dataTestId='payment-page-licence-info'
            bgcolor={palette.background.paperElevation0}
            isLargeScreen={isDesktopScreen}
          >
            <LicenceInfo
              licence={selectedLicenceModel}
              toggleSelectedLicenceModel={toggleSelectedLicenceModel}
              library={urlLibrary}
              promoCode={promoCode}
              setPromoCode={setPromoCode}
              promoCodeDiscountInfo={promoCodeDiscountInfo}
              setPromoCodeDiscountInfo={setPromoCodeDiscountInfo}
              setSelectedPaymentRhythm={setSelectedPaymentRhythm}
              selectedPaymentRhythm={selectedPaymentRhythm}
              paymentRhythms={paymentRhythms}
            />
          </ColumnContainer>
          <ColumnContainer 
            dataTestId='payment-page-form'
            bgcolor={palette.background.paperElevation2}
            isLargeScreen={isDesktopScreen}
          >
            <PaymentForm
              licence={selectedLicenceModel}
              library={urlLibrary}
              paymentMode={paymentMode}
              promoCode={promoCode}
              paymentPeriod={selectedPaymentRhythm}
              promoCodeDiscountInfo={promoCodeDiscountInfo}
              redirect={urlRedirect}
            />
          </ColumnContainer>
        </Box> 
      )}
    </Box>
  );
};

export default PaymentPage;
